import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

export const CONNECTION_HUB_PARAM = "connectionHub";

/**
 * Handles `?connectionHub=true` once per arrival, in one place.
 *
 * It replaced eight near-copies that disagreed on five details. The param is stripped before the
 * action runs and always with `replace`, so Back cannot re-trigger it and the action never sees a
 * stale URL. `action` is a callback rather than a fixed behaviour because one host keys off a second
 * param (`showProductState`) instead of opening anything.
 */
export const useConnectionHubParam = (action: (params: URLSearchParams) => void): void => {
  const [searchParams, setSearchParams] = useSearchParams();
  const actionRef = useRef(action);
  const handledRef = useRef(false);
  actionRef.current = action;

  useEffect(() => {
    if (!searchParams.get(CONNECTION_HUB_PARAM)) {
      handledRef.current = false;
      return;
    }
    if (handledRef.current) return;
    handledRef.current = true;

    const next = new URLSearchParams(searchParams);
    next.delete(CONNECTION_HUB_PARAM);
    setSearchParams(next, { replace: true });
    actionRef.current(next);
  }, [searchParams, setSearchParams]);
};

export default useConnectionHubParam;
